import ListItemButton from '@mui/material/ListItemButton';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { Box } from '../Box';
import { Text } from '../Text';

export interface ActionListItemProps {
  icon: ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
  tone?: 'default' | 'danger';
}

// A descriptive action row: a leading icon in a soft rounded container + a bold title and a
// one-line description. The façade's first List-family wrapper — a view cannot import
// @mui/material/ListItemButton directly (ADR 0004 / DESIGN.md §8), so the interactive-row
// behaviour (keyboard activation, focus ring, hover) is curated here. Semantic component → no
// `sx` prop; brand shaping (rounded icon chip, accent-tinted hover) lives here via theme tokens.
// The `danger` tone recolours the icon chip, hover tint and title to the error palette for a
// destructive action; meaning never rests on colour alone — the title and description text carry it.
// The accessible name concatenates title + description, so assistive tech announces both together.
// TODO: i18n — the caller passes already-localized title/description.
export function ActionListItem({
  icon,
  title,
  description,
  onClick,
  tone = 'default',
}: ActionListItemProps) {
  const danger = tone === 'danger';
  const accent = danger ? 'error' : 'primary';
  return (
    <ListItemButton
      // Forward the handler only when set: exactOptionalPropertyTypes rejects onClick={undefined}.
      {...(onClick ? { onClick } : {})}
      sx={{
        gap: 2,
        alignItems: 'center',
        borderRadius: 2,
        py: 1.5,
        px: 1.5,
        '&:hover': { bgcolor: (theme) => alpha(theme.palette[accent].main, 0.08) },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: danger ? (theme) => alpha(theme.palette.error.main, 0.12) : 'grey.100',
          color: `${accent}.main`,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Text variant="subtitle2" bold dense color={danger ? 'error' : 'primary'}>
          {title}
        </Text>
        <Text variant="caption" color="secondary" dense>
          {description}
        </Text>
      </Box>
    </ListItemButton>
  );
}
